import {
  ConfigInput,
  ConfigInputValues,
  DateTimePicker,
  FieldSet,
  FieldWrapper,
  FormContext,
  FormState,
  FormValues,
  getValue,
  InputFormGroup,
  InputLink,
  Select,
  TextInput,
  TextInputMultilineConfig,
  uniqueGUID,
} from "o4a-react";
import * as React from "react";
import { DetailsList, IColumn, SelectionMode } from "@fluentui/react";
import * as Messages from "../../codegen/Messages";
import { Fields as QuotaRequestFields, QuotaRequestPanel } from "../../components/QuotaRequestPanel/QuotaRequestPanel";
import { Settings, SettingsContext } from "../../console/SettingsContext";
import { ServiceProviders } from "../../constants/pluginConstants";
import {
  CreateIncidentDetailsProblemTypeEnum,
  CreateLimitItemDetails,
  LimitDefinitionSummary,
  LimitScopeValues,
} from "../../gen/clients/mchub-azure-api-client-platform";
import { SupportIssueType, SupportSeverity } from "../../helpers/resourceHelper";
import { getAzureLocationName } from "../../utils";

export const GROUP_DETAILS = "details";

const enum NewSidePanels {
  QUOTA_DETAIL = "QUOTA_DETAIL"
}

export const enum Fields {
  StartDate ="startDate",
  Description = "description",
  Quotas = "quotas",
  Severity = "severity",
}

export interface DetailsTabComponent {
  closeSidePanels: () => void;
}

export interface QuotaRequestDetails {
  name: string;
  description: string;
  location?: string;
  currentValue: number;
  currentUsed?: number;
  newValue: number;
}

export const getQuotaDetails = (
  quotaRequestDetails: QuotaRequestDetails,
): CreateLimitItemDetails => {
  let locationPart = "";
  if (quotaRequestDetails?.location) {
    const locationDisplayName = getAzureLocationName(quotaRequestDetails?.location);
    locationPart = locationDisplayName ? `${locationDisplayName}, ` : "";
  }

  // eslint-disable-next-line max-len
  const requestSummary = `${locationPart}${quotaRequestDetails?.name} ${Messages.labels.quotaUsageAndLimitAndRequested(quotaRequestDetails?.currentUsed?.toString() || Messages.common.unknown(), quotaRequestDetails?.currentValue.toString(), quotaRequestDetails?.newValue.toString())}`;
  return {
    name: requestSummary,
    limitId: quotaRequestDetails?.name,
    location: quotaRequestDetails?.location,
    currentLimit: quotaRequestDetails?.currentValue,
    currentUsage: quotaRequestDetails?.currentUsed,
    requestedLimit: quotaRequestDetails?.newValue,
  };
};

export interface DetailsTabContentProps {
  subscriptionId: string;
  issueType: CreateIncidentDetailsProblemTypeEnum | undefined;
  provider?: ServiceProviders;
  componentRef?: (component: DetailsTabComponent) => void;
}

// eslint-disable-next-line max-len
export const DetailsTabContent = ({ subscriptionId, issueType, provider, componentRef }
  : DetailsTabContentProps): JSX.Element => {
  const form = React.useContext<FormState>(FormContext);
  const [remount, setRemount] = React.useState<string>(uniqueGUID());

  const [quotaRequest, setQuotaRequest] = React.useState<QuotaRequestDetails | undefined>();
  const [sidePanelOpen, setSidePanelOpen] = React.useState<NewSidePanels | undefined>(undefined);

  const { locale } = React.useContext<Settings>(SettingsContext);

  const closeSidePanels = (): void => setSidePanelOpen(undefined);

  React.useEffect(() => {
    if (componentRef) {
      componentRef({ closeSidePanels } as DetailsTabComponent);
    }
  // Only at mount time. Others props should not have side effect beyond their initial values
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    form.setValue({} as ConfigInputValues, Fields.Quotas, GROUP_DETAILS);
    setQuotaRequest(undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptionId, provider]);

  React.useEffect(() => {
    form.setValue(undefined, Fields.StartDate, GROUP_DETAILS);
    form.setValue(undefined, Fields.Description, GROUP_DETAILS);
    form.setValue({} as ConfigInputValues, Fields.Quotas, GROUP_DETAILS);
    form.setValue(undefined, Fields.Severity, GROUP_DETAILS);

    setQuotaRequest(undefined);

    setRemount(uniqueGUID());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issueType]);

  const descriptionLabel = (): string => {
    switch (issueType) {
      case SupportIssueType.BILLING:
        return Messages.labels.provideIssueDetails();
      case SupportIssueType.TECHNICAL:
        return Messages.labels.description();
      default:
        return "";
    }
  };

  const onQuotaDetails = (): void => {
    setSidePanelOpen(NewSidePanels.QUOTA_DETAIL);
  };

  const onSubmitQuotaDetails = (formValues: FormValues): void => {
    const limit: LimitDefinitionSummary = getValue(formValues, QuotaRequestFields.LimitName, InputFormGroup)[0].data;
    const location = limit.scopeType === LimitScopeValues.LOCATION
      ? getValue(formValues, QuotaRequestFields.Region, InputFormGroup)?.[0].id
      : undefined;
    const currentLimit = parseInt(getValue(formValues, QuotaRequestFields.CurrentLimit, InputFormGroup), 10);
    const currentUsed = parseInt(getValue(formValues, QuotaRequestFields.CurrentUsed, InputFormGroup), 10);
    const newLimit = parseInt(getValue(formValues, QuotaRequestFields.NewLimit, InputFormGroup), 10);

    setQuotaRequest({
      name: limit.name || "",
      description: limit.description || "",
      location,
      currentValue: currentLimit,
      currentUsed: Number.isNaN(currentUsed) ? undefined : currentUsed,
      newValue: newLimit,
    });
    setSidePanelOpen(undefined);
  };

  const quotaDetailsColumns: IColumn[] = [
    {
      key: "name",
      name: Messages.labels.requestSummary(),
      fieldName: "name",
      data: "string",
      isResizable: true,
      isMultiline: true,
      maxWidth: 300,
      minWidth: 300,
    },
    {
      key: "requested-limit",
      name: Messages.labels.newQuotaLimit(),
      fieldName: "requestedLimit",
      data: "number",
      isResizable: true,
      minWidth: 100,
    },
  ];

  const severityOptions = [
    {
      id: SupportSeverity.HIGHEST,
      text: Messages.supportSeverity.highest(),
    },
    {
      id: SupportSeverity.HIGH,
      text: Messages.supportSeverity.high(),
    },
    {
      id: SupportSeverity.MEDIUM,
      text: Messages.supportSeverity.medium(),
    },
    {
      id: SupportSeverity.LOW,
      text: Messages.supportSeverity.low(),
    },
  ];

  return (
    <>
      <FieldSet
        header={Messages.createSupport.detailsTab.sectionTitles.tellUs()}
        description={Messages.createSupport.detailsTab.sectionDescriptions.tellUs()}
      >
        <div />
      </FieldSet>
      <FieldSet
        header={Messages.createSupport.detailsTab.sectionTitles.problemDetails()}
        description={issueType === SupportIssueType.QUOTAS
          ? Messages.createSupport.detailsTab.sectionDescriptions.problemDetails()
          : undefined}
      >
        {issueType === SupportIssueType.QUOTAS && (
          <>
            <ConfigInput
              key={`quotas-${remount}`}
              fieldName={Fields.Quotas}
              label={Messages.labels.requestDetails()}
              required
              configInputValue={{
                title: quotaRequest
                  ? Messages.configTitles.quotaRequests("1")
                  : Messages.configTitles.provideRequestDetails(),
                value: quotaRequest,
              } as ConfigInputValues}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              inputLink={{
                id: "quota-request",
                text: quotaRequest ? Messages.actions.updateDetails() : Messages.actions.enterDetails(),
                onLinkClick: onQuotaDetails,
              } as InputLink}
            />
            {quotaRequest && (
              <FieldWrapper>
                <DetailsList
                  setKey="connection-string-items"
                  items={quotaRequest ? [getQuotaDetails(quotaRequest)] : []}
                  columns={quotaDetailsColumns}
                  selectionMode={SelectionMode.none}
                />
              </FieldWrapper>
            )}
          </>
        )}
        {(issueType === SupportIssueType.BILLING || issueType === SupportIssueType.TECHNICAL) && (
          <>
            <DateTimePicker
              key={`datepicker-${remount}`}
              fieldName={Fields.StartDate}
              label={Messages.labels.problemStartDate()}
              required
              locale={locale}
            />
            <TextInput
              key={`description-${remount}`}
              fieldName={Fields.Description}
              label={descriptionLabel()}
              required
              multiline={{ rows: 5 } as TextInputMultilineConfig}
            />
          </>
        )}
        <Select
          key={`severity-${remount}`}
          fieldName={Fields.Severity}
          label={Messages.labels.severity()}
          required
          options={severityOptions}
          hideSearchBox
          defaultValue={issueType !== SupportIssueType.TECHNICAL ? [SupportSeverity.MEDIUM] : undefined}
          disabled={issueType !== SupportIssueType.TECHNICAL}
        />
      </FieldSet>
      {sidePanelOpen === NewSidePanels.QUOTA_DETAIL && provider
        && (
          <QuotaRequestPanel
            subscriptionId={subscriptionId}
            provider={provider}
            limitName={quotaRequest?.name}
            location={quotaRequest?.location}
            newLimit={quotaRequest?.newValue}
            onSubmit={onSubmitQuotaDetails}
            onClose={() => setSidePanelOpen(undefined)}
          />
        )}
    </>
  );
};
